import { useNavigate } from 'react-router';
import { Loader2, FileText, FileCheck2, FileClock, ArrowRight, Home, Briefcase } from 'lucide-react';
import { YourCheckaNavigation } from '@/components/Navbar';
import { useAppSelector } from '@/hooks/useAppStore';
import { selectCurrentUser } from '@/store/slices/authSlice';
import { useGetMedicalMetricsQuery } from '@/store/api/apiSlice';

function getInitials(firstName?: string, lastName?: string) {
    const first = firstName?.charAt(0) || '';
    const last = lastName?.charAt(0) || '';
    return (first + last).toUpperCase() || 'MD';
}

function formatCurrency(amount: number | undefined) {
    if (amount === undefined || amount === null || isNaN(amount)) return '₦0';
    return new Intl.NumberFormat('en-NG', { style: 'currency', currency: 'NGN', minimumFractionDigits: 0 }).format(
        amount,
    );
}

export default function MedicalDashboard() {
    const navigate = useNavigate();
    const currentUser = useAppSelector(selectCurrentUser);
    const { data: metrics, isLoading } = useGetMedicalMetricsQuery();

    const inv = metrics?.investigations;
    const earnings = metrics?.earnings;
    const domestic = inv?.byCategory?.domestic_staff ?? 0;
    const employment = inv?.byCategory?.employment ?? 0;

    const goToReports = () => navigate('/medical/investigations');

    return (
        <main className="w-full min-h-screen flex flex-col px-4 py-6 md:px-6 lg:px-8 xl:px-12 bg-[#EDEFF3]">
            <div className="bg-white rounded-3xl shadow-sm flex-1 flex flex-col overflow-hidden">
                <YourCheckaNavigation
                    showTitle={false}
                    showInfo={false}
                    showLocation={false}
                    notificationCount={0}
                    userName={currentUser ? `${currentUser.firstName} ${currentUser.lastName}` : 'Medical'}
                    userInitials={currentUser ? getInitials(currentUser.firstName, currentUser.lastName) : 'MD'}
                    only={true}
                    className="border-b border-gray-100"
                />

                <div className="flex-1 overflow-y-auto p-4 md:p-8 lg:p-10 flex flex-col gap-6 w-full">
                    {/* Header */}
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                        <div>
                            <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-900">
                                Welcome{currentUser?.firstName ? `, ${currentUser.firstName}` : ''}
                            </h1>
                            <p className="text-sm text-gray-500 mt-1">
                                Your assigned investigations and the reports still to enter.
                            </p>
                        </div>
                        <button
                            onClick={goToReports}
                            className="self-start sm:self-auto bg-[#f37b23] hover:bg-[#e06a15] text-white px-4 py-2.5 rounded-xl text-sm font-medium flex items-center gap-2"
                        >
                            <FileText className="h-4 w-4" />
                            Enter reports
                        </button>
                    </div>

                    {isLoading ? (
                        <div className="flex items-center justify-center py-20">
                            <Loader2 className="h-8 w-8 text-[#FF6B35] animate-spin" />
                        </div>
                    ) : (
                        <>
                            {/* Top metric cards */}
                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 md:gap-4">
                                <MetricCard
                                    icon={<FileText className="h-5 w-5 text-gray-600" />}
                                    count={inv?.total ?? 0}
                                    label="Assigned to you"
                                    tone="gray"
                                />
                                <MetricCard
                                    icon={<FileCheck2 className="h-5 w-5 text-green-600" />}
                                    count={inv?.reportSubmitted ?? 0}
                                    label="Report submitted"
                                    tone="green"
                                />
                                <MetricCard
                                    icon={<FileClock className="h-5 w-5 text-amber-600" />}
                                    count={inv?.reportPending ?? 0}
                                    label="Report pending"
                                    tone="amber"
                                    onClick={goToReports}
                                />
                            </div>

                            {/* Category split + earnings */}
                            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                                <div className="lg:col-span-2 rounded-2xl border border-gray-100 p-5 flex flex-col gap-4">
                                    <h2 className="text-sm font-bold text-gray-900">By category</h2>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                        <div className="rounded-xl bg-gray-50 px-4 py-4 flex items-center gap-3">
                                            <div className="h-10 w-10 rounded-lg bg-white flex items-center justify-center">
                                                <Home className="h-5 w-5 text-[#f37b23]" />
                                            </div>
                                            <div>
                                                <p className="text-2xl font-bold text-gray-900 leading-none">
                                                    {domestic}
                                                </p>
                                                <p className="text-xs text-gray-500 mt-1">Domestic staff</p>
                                            </div>
                                        </div>
                                        <div className="rounded-xl bg-gray-50 px-4 py-4 flex items-center gap-3">
                                            <div className="h-10 w-10 rounded-lg bg-white flex items-center justify-center">
                                                <Briefcase className="h-5 w-5 text-[#f37b23]" />
                                            </div>
                                            <div>
                                                <p className="text-2xl font-bold text-gray-900 leading-none">
                                                    {employment}
                                                </p>
                                                <p className="text-xs text-gray-500 mt-1">Employment</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Earnings */}
                                <div className="rounded-2xl border border-gray-100 p-5 flex flex-col gap-4">
                                    <div className="flex items-center justify-between">
                                        <h2 className="text-sm font-bold text-gray-900">Your pay</h2>
                                        <button
                                            onClick={() => navigate('/earnings')}
                                            className="text-xs font-medium text-[#f37b23] hover:underline flex items-center gap-1"
                                        >
                                            Details <ArrowRight className="h-3 w-3" />
                                        </button>
                                    </div>
                                    <div className="rounded-xl bg-gray-50 px-4 py-3">
                                        <p className="text-xs text-gray-500">Total</p>
                                        <p className="text-2xl font-bold text-gray-900">
                                            {formatCurrency(earnings?.total)}
                                        </p>
                                    </div>
                                    <div className="grid grid-cols-2 gap-3">
                                        <div className="rounded-xl bg-green-50 px-4 py-3">
                                            <p className="text-xs text-green-600">Paid</p>
                                            <p className="text-lg font-bold text-green-900">
                                                {formatCurrency(earnings?.paid)}
                                            </p>
                                        </div>
                                        <div className="rounded-xl bg-amber-50 px-4 py-3">
                                            <p className="text-xs text-amber-600">Pending</p>
                                            <p className="text-lg font-bold text-amber-900">
                                                {formatCurrency(earnings?.pending)}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </main>
    );
}

function MetricCard({
    icon,
    count,
    label,
    tone,
    onClick,
}: {
    icon: React.ReactNode;
    count: number;
    label: string;
    tone: 'gray' | 'green' | 'amber';
    onClick?: () => void;
}) {
    const toneBg: Record<string, string> = {
        gray: 'bg-gray-100',
        green: 'bg-green-50',
        amber: 'bg-amber-50',
    };
    const Wrapper: React.ElementType = onClick ? 'button' : 'div';
    return (
        <Wrapper
            {...(onClick ? { type: 'button', onClick } : {})}
            className={`text-left rounded-2xl border border-gray-100 p-5 flex items-center gap-4 ${
                onClick ? 'hover:border-gray-200 hover:shadow-sm transition-all' : ''
            }`}
        >
            <div className={`h-11 w-11 shrink-0 rounded-xl ${toneBg[tone]} flex items-center justify-center`}>
                {icon}
            </div>
            <div className="min-w-0">
                <p className="text-2xl font-bold text-gray-900 leading-none">{count}</p>
                <p className="text-xs text-gray-500 mt-1.5">{label}</p>
            </div>
        </Wrapper>
    );
}
